import {
	AfterViewInit,
	Component,
	Input,
	OnInit,
	OnDestroy,
	ViewChild,
	OnChanges,
	SimpleChanges,
	Output,
	EventEmitter
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { EntityAction } from '@datorama/akita';
import { Subject, takeUntil } from 'rxjs';
import { resolveDotNotationPath } from '../../../../../_core/utils/object.utils';
import { Action, ActionEvent, ActionType, Collection, CollectionTemplate } from '../../collection.model';
import { CollectionUtils } from '../../collection.utils';
import { TableCollectionTemplate, Column } from './table.model';
import { UserRole } from '../../../../../../../../api/src/user/user.entity';

@Component({
	selector: 'app-table',
	templateUrl: './table.component.html',
	styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnChanges, OnDestroy {
	@Input() template: TableCollectionTemplate;
	@Input() entities: any[];
	@Input() page = 1;
	@Input() limit = 10;
	@Input() limitOptions = [10, 25, 50, 100];
	@Input() totalResults = 0;
	@Input() selectedEntities: any[] = [];
	@Input() loadingEntities: any[] = [];
	@Input() scrollableTable: boolean = false;
	@Input() showPaginator: boolean = true;

	@Output() updatePagination: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
	@Output() updateSort: EventEmitter<Sort> = new EventEmitter<Sort>();
	@Output() action: EventEmitter<ActionEvent> = new EventEmitter<ActionEvent>();
	@Output() clickOnCreate: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() clickOnLinkedColumn: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	public readonly ActionType = ActionType;
	public resolveDotNotationPath = resolveDotNotationPath;
	public collectionUtils = CollectionUtils;
	public dataSource: MatTableDataSource<any> = new MatTableDataSource([]);
	public activeColumnIds: string[];

	private unsubscribe$: Subject<void> = new Subject();

	public superAdmin = UserRole.SuperAdmin;
	
	enableReportingText: string = $localize`:User Settings@@enableReporting:Enable reporting`;
	disableReportingText: string = $localize`:User Settings@@disableReporting:Disable reporting`;
	markAsJoinable: string = $localize`:User Settings@@markJoinable:Mark as joinable`;// mark as joinable
	unmarkAsJoinable: string = $localize`:User Settings@@unmarkJoinable:Unmark as joinable`;//unmark as joinable


	constructor() {}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.entities) {
			if (changes.entities.currentValue) {
				this.dataSource.data = changes.entities.currentValue;
			}
		}

		if (changes.template) {
			// Set the active columns
			this.activeColumnIds = changes.template.currentValue.settings.activeColumns.map(col => col.id);

			if (changes.template.currentValue.settings.actions?.length) {
				this.activeColumnIds = this.activeColumnIds.concat('actions');
			}


			if (changes.template.currentValue.settings.limitOptions) {
				this.limitOptions = changes.template.currentValue.settings.limitOptions;
			}
		}
	}

	ngAfterViewInit(): void {
		// Check template for necessary data
		if (this.inputsHaveErrors()) {
			return;
		}

		this.dataSource.data = this.entities || [];
		console.log('🗂️ TableComponent entities set:', this.entities?.length || 0, 'First entity:', this.entities?.[0] ? { name: this.entities[0].name, isPinned: this.entities[0].isPinned } : 'No entities');
	}

	inputsHaveErrors() {
		if (!this.template) {
			console.error('TableContainerComponent: No template input defined', this.template);
			return true;
		}

		if (!this.template.settings.columns) {
			console.error('TableContainerComponent: No columns defined in template', this.template);
			return true;
		}

		if (!this.template.settings.activeColumns) {
			console.error('TableContainerComponent: No activeColumns defined in template', this.template);
			return true;
		}
	}

	isSelected(entityId: any) {
		return this.selectedEntities.findIndex(entity => entity.id === entityId) > -1;
	}

	isLoading(entityId: any) {
		return this.loadingEntities.findIndex(entity => entity.id === entityId) > -1;
	}

	onActionEvent(entity: any, action: Action) {
		console.log('⚡ TableComponent.onActionEvent:', action.type, 'Entity:', { name: entity.name, isPinned: entity.isPinned });
		this.action.emit({
			entity,
			action
		});
	}

	onRowClick(entity: any) {
		// Check if we have a checkbox column type
		const checkboxColumn = this.template.settings.activeColumns.find(col => col.type === 'checkbox');
		
		if (checkboxColumn) {
			// When we have a checkbox column, trigger its action when row is clicked
			if (checkboxColumn.extra?.action) {
				this.onActionEvent(entity, checkboxColumn.extra.action);
			}
		} else {
			// Check if we have a selection column on the left  
			const hasLeftCheckbox = this.template.settings.activeColumns.some(col => col.id === 'selection');
			
			if (hasLeftCheckbox) {
				// Emit a select action event when row is clicked
				const selectAction: Action = {
					id: 'select',
					name: 'Select',
					type: ActionType.SELECT,
					icon: 'check_box_outline_blank',
					toggleOptions: {
						onIcon: 'check_box',
						onName: ''
					}
				};
				this.action.emit({ entity, action: selectAction });
			}
		}
	}

	ngOnDestroy() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	trackByFn(index, item) {
		return item.id;
	}

	showCreateButton(): Action {
		return this.template.settings.actions?.find(action => action.id === 'create');
	}

	linkEvent(column: Column, entity: any) {
		if (column.linked?.bubble) this.clickOnLinkedColumn.emit({ action: { id: 'edit' }, entity });
		if (column.linked?.routerLink)
			this.clickOnLinkedColumn.emit({
				action: { id: 'goTo', type: 'goTo' },
				routerLink: column.linked.routerLink,
				entity
			});
	}
}
